import { useState, useEffect, useCallback } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { loginRequest } from '../services/auth';
import type { AdminIdentity } from '../types';

interface UseAuthResult {
  isAuthenticated: boolean;
  isLoading: boolean;
  admin: AdminIdentity | null;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  error: string | null;
}

export function useAuth(): UseAuthResult {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [error, setError] = useState<string | null>(null);

  const [admin, setAdmin] = useState<AdminIdentity | null>(null);

  useEffect(() => {
    if (isAuthenticated && accounts.length > 0) {
      const account = accounts[0];
      setAdmin({
        id: account.localAccountId,
        displayName: account.name || account.username,
        userPrincipalName: account.username,
        mail: account.username,
        tenantId: account.tenantId,
      });
    } else {
      setAdmin(null);
    }
  }, [isAuthenticated, accounts]);

  const signIn = useCallback(async () => {
    try {
      setError(null);
      await instance.loginPopup(loginRequest);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sign in failed';
      // User cancelling the popup is not an error
      if (!message.includes('user_cancelled')) {
        setError(message);
      }
    }
  }, [instance]);

  const signOut = useCallback(async () => {
    try {
      await instance.logoutPopup();
    } catch (err) {
      console.error('Logout failed:', err);
    }
  }, [instance]);

  return {
    isAuthenticated,
    isLoading: inProgress !== 'none',
    admin,
    signIn,
    signOut,
    error,
  };
}
